import _ from 'lodash'
import wAxios from '@/plugins/w/axios/models/axios'
import {
	DEFAULT_FILTERS,
	fetchQuery,
	type ResultsFilters
} from '@/plugins/app@lz/result/_services/results.service'
import type { TokenHandshakeHandler } from './types'

const buildV1UnreadResultsQuery = (
	filters: ResultsFilters,
	pagination: Pagination
): string => {
	const params = new URLSearchParams({
		has_result: 'true',
		readed: 'false'
	})

	params.set('per_page', String(pagination.per_page))
	params.set('page', String(pagination.current_page))

	const search = filters.search?.trim()
	if (search) {
		params.set('search', search)
	}

	if (filters.date_sent) {
		params.set('created_from', filters.date_sent)
		params.set('created_to', filters.date_sent)
	} else if (filters.last_result?.start && filters.last_result?.end) {
		params.set('last_result_from', filters.last_result.start)
		params.set('last_result_to', filters.last_result.end)
	}

	filters.accesses?.forEach((access, index) => {
		params.append(`accesses[${index}]`, String(access))
	})

	return params.toString()
}

const parseReaded = (value: unknown): boolean | undefined => {
	if (value === undefined || value === null) return undefined
	if (typeof value === 'boolean') return value
	if (value === 'true' || value === '1') return true
	if (value === 'false' || value === '0') return false
	return Boolean(value)
}

const parseFilters = (params?: Record<string, unknown>): ResultsFilters =>
	_.defaults(
		{
			...(params?.search != null && { search: String(params.search) }),
			...(Array.isArray(params?.accesses) && {
				accesses: params.accesses.map(Number)
			}),
			...(params?.readed !== undefined && { readed: parseReaded(params.readed) }),
			...(params?.date_sent != null && { date_sent: String(params.date_sent) }),
			...(params?.last_result != null &&
				typeof params.last_result === 'object' && {
					last_result: params.last_result as ResultsFilters['last_result']
				}),
			...(params?.sort != null &&
				typeof params.sort === 'object' && {
					sort: params.sort as ResultsFilters['sort']
				}),
			...(Array.isArray(params?.labs) && { labs: params.labs.map(String) })
		},
		DEFAULT_FILTERS
	)

export const getPatientResultsHandler: TokenHandshakeHandler = async (params, { authStore }) => {
	const perPage = Number(params?.per_page) || 10
	const currentPage = Number(params?.current_page) || 1

	const pagination: Pagination = {
		per_page: perPage,
		current_page: currentPage,
		last_page: 1
	}

	const filters = parseFilters(params)

	if (!filters.accesses?.length) {
		const currentAccess = authStore.getCurrentAccess
		filters.accesses = currentAccess ? [currentAccess.id] : []
	}

	if (filters.accesses.length === 0) {
		return {
			result: {
				data: [],
				meta: { last_page: 1 }
			}
		}
	}

	const response =
		filters.readed === false
			? await wAxios
					.get_auth(`v1/requests?${buildV1UnreadResultsQuery(filters, pagination)}`)
					.catch(error => Promise.reject(error.response || error))
			: await wAxios
					.post_auth('v2/requests', fetchQuery(pagination, filters))
					.catch(error => Promise.reject(error.response || error))

	return {
		result: {
			data: response.data,
			meta: response.meta
		}
	}
}
